import React, { useEffect, useState, useRef } from 'react';
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

/**
 * EEGDeviceRegister.jsx
 * Single-file React component (React 16 compatible) that shows a simple
 * user registration screen for Wi-Fi credentials (SSID / Password)
 * and a nearby COM-port list (uses Web Serial API when available).
 *
 * Features:
 * - SSID / password form with basic validation
 * - Button to save / submit credentials to backend (/api/register - POST JSON)
 * - Detect available serial ports using navigator.serial.getPorts()
 * - Let the user open a system picker to grant access to a serial port
 *   (navigator.serial.requestPort)
 * - Fallback to an optional backend endpoint (/api/serial/ports) if Web Serial
 *   is not available or the page is served in an environment without it.
 * - Small toast notifications for success / error
 *
 * Notes:
 * - Web Serial API requires a secure context (https or localhost) and user
 *   gesture for navigator.serial.requestPort() to show the picker.
 * - If you need to enumerate ALL OS COM ports without user picking, implement
 *   a backend API (example route: GET /api/serial/ports) that calls SerialPort
 *   enumeration on the server (e.g., using System.IO.Ports on .NET) and return
 *   the list. The `fetchPortsFromBackend()` helper will try that if available.
 */

export default function EEGDeviceRegister({ backendListPortsUrl = '/api/serial/ports', submitUrl = '/api/register' }) {
  // form
  const [ssid, setSsid] = useState('');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);

  // ports
  const [ports, setPorts] = useState([]); // {id, info, granted, open}
  const [loadingPorts, setLoadingPorts] = useState(false);

  // toasts
  const [toasts, setToasts] = useState([]);
  const toastsRef = useRef([]);
  toastsRef.current = toasts;
  const showToast = (message, type = 'info', t = 3500) => {
    const id = Date.now() + Math.floor(Math.random() * 999);
    const toast = { id, message, type, visible: true };
    setToasts(prev => [toast, ...prev]);
    setTimeout(() => removeToast(id), t);
  };
  const removeToast = (id) => {
    setToasts(prev => prev.map(x => x.id === id ? { ...x, visible: false } : x));
    setTimeout(() => setToasts(prev => prev.filter(x => x.id !== id)), 220);
  };

  // helper: try Web Serial API getPorts
  const fetchPortsUsingWebSerial = async () => {
    if (!('serial' in navigator)) return null;
    try {
      setLoadingPorts(true);
      const grantedPorts = await navigator.serial.getPorts();
      // Map to simple info objects; actual SerialPort object can't be serialized
      const mapped = grantedPorts.map((p, idx) => ({
        id: `grant-${idx}`,
        granted: true,
        portObj: p,
        info: p.getInfo ? p.getInfo() : null,
        label: getPortLabel(p, idx)
      }));
      setPorts(mapped);
      return mapped;
    } catch (err) {
      console.warn('WebSerial getPorts error', err);
      return null;
    } finally {
      setLoadingPorts(false);
    }
  };

  // helper: request a port (user picks one) - must be called from user gesture
  const requestPort = async () => {
    if (!('serial' in navigator)) {
      showToast('Web Serial API not available in this browser', 'error');
      return;
    }
    try {
      const p = await navigator.serial.requestPort();
      // add to list
      const id = `req-${Date.now()}`;
      const entry = { id, granted: true, portObj: p, info: p.getInfo ? p.getInfo() : null, label: getPortLabel(p, id) };
      setPorts(prev => [entry, ...prev]);
      showToast('Port granted by user', 'success');
    } catch (err) {
      console.warn('requestPort cancelled or failed', err);
      showToast('Port chooser cancelled', 'info');
    }
  };

  // fallback: call backend to enumerate COM ports (requires server support)
  const fetchPortsFromBackend = async () => {
    if (!backendListPortsUrl) return null;
    try {
      setLoadingPorts(true);
      const r = await fetch(backendListPortsUrl);
      if (!r.ok) throw new Error('backend port list failed');
      const json = await r.json(); // expect array like [{ name: 'COM3', friendly: 'USB Serial' }, ...]
      const mapped = json.map((p, idx) => ({ id: `srv-${idx}-${p.name || idx}`, granted: false, portObj: null, info: p, label: `${p.name || p.path || 'COM?'} ${p.friendly ? '- ' + p.friendly : ''}` }));
      setPorts(mapped);
      return mapped;
    } catch (err) {
      console.warn('backend ports fetch failed', err);
      return null;
    } finally {
      setLoadingPorts(false);
    }
  };

  // helper label
  const getPortLabel = (portObj, idx) => {
    try {
      if (!portObj) return `COM-${idx}`;
      const info = portObj.getInfo ? portObj.getInfo() : null;
      if (info && (info.usbProductId || info.usbVendorId)) return `USB VID:${info.usbVendorId || '?'} PID:${info.usbProductId || '?'} (serial)`;
      return `Serial port ${idx}`;
    } catch (e) {
      return `Serial port ${idx}`;
    }
  };

  // initial port load: try WebSerial first then backend
  useEffect(() => {
    let mounted = true;
    (async () => {
      if ('serial' in navigator) {
        const x = await fetchPortsUsingWebSerial();
        if (mounted && x && x.length) return; // we have ports
      }
      // fallback
      if (mounted) await fetchPortsFromBackend();
    })();
    return () => { mounted = false; };
  }, []);

  // allow manual refresh
  const refreshPorts = async () => {
    setPorts([]);
    if ('serial' in navigator) await fetchPortsUsingWebSerial();
    await fetchPortsFromBackend();
  };

  // open/close port (for demo only) - opens at 115200
  const openPort = async (entry) => {
    if (!entry || !entry.portObj) return showToast('No port object to open', 'error');
    try {
      await entry.portObj.open({ baudRate: 115200 });
      // mark open
      setPorts(prev => prev.map(p => p.id === entry.id ? { ...p, open: true } : p));
      showToast('Port opened (115200)', 'success');
    } catch (err) {
      console.error('open port failed', err);
      showToast('Failed to open port: ' + (err && err.message ? err.message : ''), 'error');
    }
  };

  const closePort = async (entry) => {
    if (!entry || !entry.portObj) return;
    try {
      await entry.portObj.close();
      setPorts(prev => prev.map(p => p.id === entry.id ? { ...p, open: false } : p));
      showToast('Port closed', 'info');
    } catch (err) {
      console.error('close failed', err);
      showToast('Close failed', 'error');
    }
  };

  // submit SSID/password to server
  const submit = async (e) => {
    e && e.preventDefault && e.preventDefault();
    if (!ssid) return showToast('Enter SSID', 'error');
    setSaving(true);
    try {
      const r = await fetch(submitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ssid, password })
      });
      if (!r.ok) throw new Error('Server returned ' + r.status);
      showToast('Saved credentials', 'success');
    } catch (err) {
      console.error('submit error', err);
      showToast('Save failed: ' + (err.message || err), 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div style={{ padding: 16, fontFamily: 'system-ui, Arial, sans-serif', maxWidth: 980, margin: '12px auto', width: '100%', boxSizing: 'border-box' }}>
      <h2 style={{ fontSize: 18, marginBottom: 6 }}>Device Registration</h2>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {/* left: Wi-Fi form */}
        <div style={{ flex: 1, background: '#fff', padding: 12, borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.04)' }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Wi‑Fi credentials</div>
          <form onSubmit={submit}>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>SSID</label>
              <input value={ssid} onChange={e => setSsid(e.target.value)} placeholder="Your Wi‑Fi SSID" style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #e6e6e6' }} />
            </div>
            <div style={{ marginBottom: 8 }}>
              <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Password</label>
              <input value={password} onChange={e => setPassword(e.target.value)} placeholder="Wi‑Fi password" type="password" style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #e6e6e6' }} />
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button type="submit" disabled={saving} style={{ padding: '8px 12px', background: '#2b86d6', color: '#fff', border: 'none', borderRadius: 6 }}>Save</button>
              <button type="button" onClick={() => { setSsid(''); setPassword(''); }} style={{ padding: '8px 12px', background: '#f0f0f0', border: 'none', borderRadius: 6 }}>Clear</button>
            </div>
          </form>

          <div style={{ marginTop: 12, fontSize: 12, color: '#666' }}>
            The credentials will be POSTed to <code>{submitUrl}</code>. Adjust `submitUrl` prop if needed.
          </div>
        </div>

        {/* right: COM ports */}
        <div style={{ width: 420, background: '#fff', padding: 12, borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.04)' }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>Nearby COM ports</div>

          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <button onClick={requestPort} style={{ padding: '8px 10px', borderRadius: 6, background: '#27ae60', color: '#fff', border: 'none' }}>Choose port (system picker)</button>
            <button onClick={refreshPorts} style={{ padding: '8px 10px', borderRadius: 6, background: '#f0f0f0', border: 'none' }}>{loadingPorts ? 'Refreshing...' : 'Refresh'}</button>
          </div>

          <div style={{ fontSize: 13, color: '#333', marginBottom: 8 }}>Detected ports:</div>

          <div style={{ maxHeight: 260, overflow: 'auto', borderTop: '1px solid #eee', paddingTop: 8 }}>
            {ports.length === 0 && (
              <div style={{ color: '#888', fontSize: 13 }}>No ports detected. If your browser doesn't support Web Serial, the list may come from the server.</div>
            )}

            {ports.map(p => (
              <div key={p.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed #f0f0f0' }}>
                <div>
                  <div style={{ fontWeight: 700 }}>{p.label}</div>
                  <div style={{ fontSize: 12, color: '#666' }}>{p.info && typeof p.info === 'object' ? JSON.stringify(p.info) : (p.info ? String(p.info) : '')}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {p.portObj && !p.open && <button onClick={() => openPort(p)} style={{ padding: '6px 8px', borderRadius: 6, background: '#2b86d6', color: '#fff', border: 'none' }}>Open</button>}
                  {p.portObj && p.open && <button onClick={() => closePort(p)} style={{ padding: '6px 8px', borderRadius: 6, background: '#f39c12', color: '#fff', border: 'none' }}>Close</button>}
                </div>
              </div>
            ))}
          </div>

          <div style={{ marginTop: 10, fontSize: 12, color: '#666' }}>
            Note: Web Serial API requires user gesture to pick a port. To enumerate all OS ports without user interaction, expose a server endpoint that lists ports.
          </div>
        </div>
      </div>

      {/* Toasts */}
      <div style={{ position: 'fixed', right: 20, top: 20, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none' }}>
        {toasts.map(t => (
          <div key={t.id} style={{ pointerEvents: 'auto', minWidth: 220, maxWidth: 360, padding: '10px 12px', borderRadius: 8, color: '#fff', background: t.type === 'success' ? '#27ae60' : (t.type === 'error' ? '#c0392b' : '#2b86d6'), boxShadow: '0 6px 18px rgba(0,0,0,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', transform: t.visible ? 'translateY(0)' : 'translateY(-8px)', opacity: t.visible ? 1 : 0, transition: 'transform 200ms ease, opacity 200ms ease' }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              <div style={{ width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.12)' }}>{t.type === 'success' ? '✔' : (t.type === 'error' ? '✖' : 'ℹ')}</div>
              <div style={{ lineHeight: 1 }}>{t.message}</div>
            </div>
            <button onClick={() => removeToast(t.id)} style={{ marginLeft: 12, border: 'none', background: 'transparent', color: 'rgba(255,255,255,0.9)', cursor: 'pointer', fontSize: 14, padding: 6 }}>✖</button>
          </div>
        ))}
      </div>
      </div>
    </DashboardLayout>
  );
}
